import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

/**
 * M11 L11.02 — Verification detail / checklist / photos / status transitions.
 *
 * Reads detail from `GET /v1/closure/review/:verificationId` (returns
 * { verification, checklist, photos, approvals, history }).
 *
 * Provides per-criterion score input (write via
 * `POST /v1/closure/review/:verificationId/checklist`), photo type tally
 * (write via `POST /v1/closure/review/:verificationId/photo`) and the
 * IN_REVIEW → IN_DECISION transition button.
 */
interface DetailResponse {
  readonly verification: {
    readonly verificationId: string;
    readonly status: string;
    readonly cycleNo: number;
    readonly overallQualityScore: number | null;
    readonly brandComplianceScore: number | null;
    readonly photoComplianceScore: number | null;
    readonly gpsValidated: boolean | null;
    readonly areaVariancePct: number | null;
  };
  /** The job's number — how the job is known on screen. */
  readonly jobNumber: string | null;
  readonly checklist: ReadonlyArray<{
    readonly checklistId: string;
    readonly criterionCode: string;
    readonly score: number;
    readonly remarks: string | null;
  }>;
  readonly photos: ReadonlyArray<{
    readonly photoId: string;
    readonly photoTypeCode: string;
    readonly storageUri: string | null;
  }>;
  readonly approvals: ReadonlyArray<unknown>;
  readonly history: ReadonlyArray<unknown>;
}

const CRITERIA = [
  'EXECUTION_QUALITY',
  'BRAND_COMPLIANCE',
  'PHOTO_COMPLIANCE',
  'GPS_COMPLIANCE',
  'AREA_COMPLIANCE',
  'DOCUMENTATION',
] as const;

const PHOTO_TYPES = ['BEFORE', 'AFTER', 'LOCATION_CONTEXT', 'DURING', 'REWORK'] as const;

export function ClosureReviewPage(): ReactNode {
  const { decimal } = useBusinessNumber();
  const { t } = useTranslation();
  const { verificationId = '' } = useParams<{ verificationId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<boolean>(false);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiRequest<DetailResponse>(
        `/v1/closure/review/${encodeURIComponent(verificationId)}`,
        { token },
      );
      setData(d);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, verificationId]);

  useEffect(() => {
    if (!verificationId) return;
    void load();
  }, [verificationId, load]);

  const upsertCriterion = useCallback(
    async (code: string, score: number, remarks: string) => {
      setBusy(true);
      setInfo(null);
      try {
        await apiRequest(
          `/v1/closure/review/${encodeURIComponent(verificationId)}/checklist`,
          {
            method: 'POST',
            token,
            body: { criterionCode: code, score, remarks },
          },
        );
        setInfo(`Saved ${code}`);
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [token, verificationId, load],
  );

  const addPhoto = useCallback(
    async (photoTypeCode: string) => {
      setBusy(true);
      setInfo(null);
      try {
        await apiRequest(
          `/v1/closure/review/${encodeURIComponent(verificationId)}/photo`,
          {
            method: 'POST',
            token,
            body: { photoTypeCode, storageUri: `mock://photo/${photoTypeCode}` },
          },
        );
        setInfo(`Added ${photoTypeCode}`);
        await load();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [token, verificationId, load],
  );

  const toInDecision = useCallback(async () => {
    setBusy(true);
    setInfo(null);
    try {
      await apiRequest(
        `/v1/closure/review/${encodeURIComponent(verificationId)}/in-decision`,
        { method: 'POST', token, body: {} },
      );
      setInfo('Transitioned to IN_DECISION');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, [token, verificationId, load]);

  if (error && !data) {
    return (
      <main aria-label="closure-review">
        <p role="alert">{error}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main aria-label="closure-review">
        <p>{t('closure.review.loading')}</p>
      </main>
    );
  }

  const v = data.verification;
  const checklistByCode = new Map(data.checklist.map((c) => [c.criterionCode, c]));
  const photoCounts = PHOTO_TYPES.map((t) => ({
    type: t,
    count: data.photos.filter((p) => p.photoTypeCode === t).length,
  }));

  return (
    <main aria-label="closure-review">
      <h1>
        {t('closure.review.title')} <span aria-label="job-id">{data.jobNumber ?? '—'}</span>
      </h1>
      <p>
        {t('closure.review.status')} <span aria-label="status">{v.status}</span>, {t('closure.review.cycle')}{' '}
        <span aria-label="cycle">{v.cycleNo}</span>
      </p>

      {error && <p role="alert">{error}</p>}
      {info && <p role="status">{info}</p>}

      <section aria-label="score-summary">
        <p aria-label="score-quality">{t('closure.review.quality')}: {decimal(v.overallQualityScore)}</p>
        <p aria-label="score-brand">{t('closure.review.brand')}: {decimal(v.brandComplianceScore)}</p>
        <p aria-label="score-photo">{t('closure.review.photo')}: {decimal(v.photoComplianceScore)}</p>
        <p aria-label="gps">{t('closure.review.gps')}: {v.gpsValidated === null ? '—' : v.gpsValidated ? t('closure.review.ok') : t('closure.review.fail')}</p>
        <p aria-label="area">{t('closure.review.areaVariance')}: {decimal(v.areaVariancePct)}</p>
      </section>

      <section aria-label="checklist">
        <h2>{t('closure.review.checklistH')}</h2>
        <table aria-label="checklist-table">
          <thead>
            <tr>
              <th>{t('closure.review.col.criterion')}</th>
              <th>{t('closure.review.col.score')}</th>
              <th>{t('closure.review.col.remarks')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {CRITERIA.map((code) => {
              const row = checklistByCode.get(code);
              return (
                <CriterionRow
                  key={code}
                  code={code}
                  initialScore={row?.score ?? 0}
                  initialRemarks={row?.remarks ?? ''}
                  disabled={busy}
                  onSave={upsertCriterion}
                />
              );
            })}
          </tbody>
        </table>
      </section>

      <section aria-label="photos">
        <h2>{t('closure.review.photosH')}</h2>
        <ul>
          {photoCounts.map((p) => (
            <li key={p.type} aria-label={`photo-${p.type}`}>
              {p.type}: {p.count}{' '}
              <button
                type="button"
                aria-label={`add-${p.type}`}
                disabled={busy}
                onClick={() => void addPhoto(p.type)}
              >
                {t('closure.review.add')}
              </button>
            </li>
          ))}
        </ul>
      </section>

      <section aria-label="decision-actions">
        <button
          type="button"
          aria-label="to-in-decision"
          disabled={busy || v.status !== 'IN_REVIEW'}
          onClick={() => void toInDecision()}
        >
          {t('closure.review.toInDecision')}
        </button>
        <Link to={`/closure/company-approval/${v.verificationId}`} aria-label="go-approval">
          {t('closure.review.openApproval')}
        </Link>
      </section>

      <section aria-label="validation-links" style={{ marginTop: 16 }}>
        <h2>{t('closure.review.validationH')}</h2>
        <ul>
          <li><Link to={`/closure/photos/${v.verificationId}`} aria-label="go-photos">{t('closure.review.link.photo')}</Link></li>
          <li><Link to={`/closure/gps/${v.verificationId}`} aria-label="go-gps">{t('closure.review.link.gps')}</Link></li>
          <li><Link to={`/closure/area/${v.verificationId}`} aria-label="go-area">{t('closure.review.link.area')}</Link></li>
          <li><Link to={`/closure/compliance/${v.verificationId}`} aria-label="go-compliance">{t('closure.review.link.compliance')}</Link></li>
          <li><Link to={`/closure/rework/${v.verificationId}`} aria-label="go-rework">{t('closure.review.link.rework')}</Link></li>
          <li><Link to={`/closure/evidence/${v.verificationId}`} aria-label="go-evidence">{t('closure.review.link.evidence')}</Link></li>
          <li><Link to={`/closure/quality/${v.verificationId}`} aria-label="go-quality">{t('closure.review.link.quality')}</Link></li>
        </ul>
      </section>
    </main>
  );
}

interface CriterionRowProps {
  readonly code: string;
  readonly initialScore: number;
  readonly initialRemarks: string;
  readonly disabled: boolean;
  readonly onSave: (code: string, score: number, remarks: string) => Promise<void>;
}

function CriterionRow(props: CriterionRowProps): ReactNode {
  const { t } = useTranslation();
  const [score, setScore] = useState<number>(props.initialScore);
  const [remarks, setRemarks] = useState<string>(props.initialRemarks);
  return (
    <tr aria-label={`crit-${props.code}`}>
      <td>{props.code}</td>
      <td>
        <input
          type="number"
          min={0}
          max={100}
          aria-label={`score-${props.code}`}
          value={score}
          onChange={(e) => setScore(Number(e.target.value))}
        />
      </td>
      <td>
        <input
          type="text"
          aria-label={`remarks-${props.code}`}
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
        />
      </td>
      <td>
        <button
          type="button"
          aria-label={`save-${props.code}`}
          disabled={props.disabled}
          onClick={() => void props.onSave(props.code, score, remarks)}
        >
          {t('closure.review.save')}
        </button>
      </td>
    </tr>
  );
}
